import { createApi } from '@reduxjs/toolkit/query/react';
import { IPhotoGeneratorFormValues } from 'modals/PhotoGeneratorModal/form';
import { ICharacter, ICharacterListItem, IPhoto } from 'types/characters';

import { createBaseQuery } from './createBaseQuery';

export interface IGeneratePhotoResponse {
  url: string;
  photoId: string;
}

export interface IRatePhotoParams {
  type?: string | null;
  comment?: string;
}

export interface IRatePhotoResponse {
  message: string;
  photo: {
    id: string;
    likesCount: number;
    dislikesCount: number;
  };
}

export interface IScenarioCharacter {
  name: string;
  about: string;
  // ... добавьте другие поля, если они нужны
}

export const charactersApi = createApi({
  reducerPath: 'api',
  baseQuery: createBaseQuery(),
  endpoints: (builder) => ({
    getCharacters: builder.query<ICharacterListItem[], void>({
      query: () => ({
        url: '/characters',
        method: 'GET',
      }),
    }),

    getCharacterById: builder.query<ICharacter, string>({
      query: (id) => ({
        url: `/characters/${id}`,
        method: 'GET',
      }),
    }),

    generateCharacterPhoto: builder.mutation<IGeneratePhotoResponse, { id: string; params: IPhotoGeneratorFormValues }>({
      query: ({ id, params }) => ({
        url: `/characters/${id}/userPhotoGen`,
        method: 'POST',
        body: params,
        requiresAuth: true,
      }),
    }),

    rateGeneratedPhoto: builder.mutation<IRatePhotoResponse, { characterId: string; photoId: string; params: IRatePhotoParams }>({
      query: ({ characterId, photoId, params }) => ({
        url: `/characters/${characterId}/photo/${photoId}/rate`,
        method: 'POST',
        body: params,
        requiresAuth: true,
      }),
    }),

    createCharacter: builder.mutation<ICharacter, ICharacter>({
      query: (character) => ({
        url: '/admin/characters',
        method: 'POST',
        body: character,
        requiresAuth: true,
      }),
    }),

    updateCharacterDescription: builder.mutation<ICharacter, ICharacter>({
      query: (character) => ({
        url: `/admin/characters/${character.id}`,
        method: 'PUT',
        body: character,
        requiresAuth: true,
      }),
    }),

    deleteCharacter: builder.mutation<void, string>({
      query: (id) => ({
        url: `/admin/characters/${id}`,
        method: 'DELETE',
        requiresAuth: true,
      }),
    }),

    uploadPhotoToCharacter: builder.mutation<IPhoto, { id: string; params: Omit<IPhoto, 'id'> }>({
      query: ({ id, params }) => ({
        url: `/admin/characters/${id}/photos`,
        method: 'POST',
        body: params,
        requiresAuth: true,
      })
    }),

    editCharacterPhoto: builder.mutation<IPhoto, { id: string; params: { id: string; url: string; shouldBlur: boolean } }>({
      query: ({ id, params }) => ({
        url: `/admin/characters/${id}/photos/${params.id}`,
        method: 'PUT',
        body: params,
        requiresAuth: true,
      }),
    }),

    deletePhotoFromCharacter: builder.mutation<void, { characterId: string; photoId: string }>({
      query: ({ characterId, photoId }) => ({
        url: `/admin/characters/${characterId}/photos/${photoId}`,
        method: 'DELETE',
        requiresAuth: true,
      }),
    }),

    getCharactersByScenarios: builder.mutation<IScenarioCharacter[], string[]>({
      query: (scenarioIds) => ({
        url: '/scenario-characters/by-scenarios',
        method: 'POST',
        body: { scenarioIds },
        requiresAuth: true,
      }),
    }),
  }),
});

export const {
  useGetCharactersQuery,
  useGetCharacterByIdQuery,
  useGenerateCharacterPhotoMutation,
  useRateGeneratedPhotoMutation,
  useCreateCharacterMutation,
  useUpdateCharacterDescriptionMutation,
  useDeleteCharacterMutation,
  useUploadPhotoToCharacterMutation,
  useEditCharacterPhotoMutation,
  useDeletePhotoFromCharacterMutation,
  useLazyGetCharactersQuery,
  useLazyGetCharacterByIdQuery,
  useGetCharactersByScenariosMutation,
} = charactersApi;
